import React, { Component } from "react";
import ThemeProvider from "elevate-ui/ThemeProvider";
import axios from "axios";
import { zIndex } from "./util";
import { generatePalette } from "elevate-ui/Color";

const GlobalContext = React.createContext({
  BATHS_OPTIONS: [],
  BEDS_OPTIONS: [],
  CDN_IMAGE_SERVER_URL: "",
  PRICE_OPTIONS: [],
  RECAPTCHA_CLIENT: "",
  RECAPTCHA_CLIENT_V2: "",
  aboutLink: "",
  aboutPosts: [],
  agent: {},
  compliance: {},
  favorites: [],
  footerPod: "",
  headerNav: "",
  isEqualHousingOpportunity: true,
  mapProvider: {},
  office: {},
  onCloseDropdown: () => null,
  onToggleDropdown: () => null,
  openDropdowns: {},
  openHouses: [],
  pathName: "",
  previousFormFieldValues: {},
  privacyPolicy: {},
  accessibility: {},
  site: {},
  siteUser: {},
  team: {},
  tenantConfig: {},
  brokers: [],
  toggleFavoriteListing: () => {},
  isCanadian: () => {},
  trackLeadAction: () => {},
  assertCaptcha: () => "",
  assertCaptchaV2: () => "",
  isPageForceV2Captcha: () => false,
  onTrackCampaign: () => null,
  onReadCampaign: () => null,
  leadCampaignTracking: {},
});

export class GlobalProvider extends Component {
  constructor(props) {
    super(props);

    let pdpNavigation = {
      navigateBack: this.navigateBack,
      navigateNext: this.navigateNext,
      navigatePrevious: this.navigatePrevious,
      showNext: false,
      showPrevious: false,
    };

    // Server-side rendering doesn't need to be aware of the navigation stack
    if (global.window) {
      let pdpStack;
      try {
        pdpStack = localStorage.getItem("pdpStack");
      } catch (e) {}

      if (pdpStack) {
        pdpStack = JSON.parse(pdpStack);
        if (pdpStack.length > 0) {
          // Check if stack is empty
          pdpNavigation.showPrevious = true;
          pdpNavigation.showNext = true;

          let url = pdpStack[pdpStack.length - 1].url;

          // in case browser back button was clicked
          if (url === window.location.href) {
            pdpStack.pop();
            localStorage.setItem("pdpStack", JSON.stringify(pdpStack));
          }

          if (pdpStack.length > 0) {
            // stack may be empty if we just pop'ed from it
            const pdpList = pdpStack[pdpStack.length - 1].pdpList;
            const action = pdpStack[pdpStack.length - 1].action;
            url = pdpStack[pdpStack.length - 1].url;

            if (pdpList.length <= 1) {
              pdpNavigation.showPrevious = false;
              pdpNavigation.showNext = false;
            }

            const currentIndex = pdpList.indexOf(window.location.pathname);

            if (action !== "search") {
              if (currentIndex === 0) {
                pdpNavigation.showPrevious = false;
              }

              if (currentIndex === pdpList.length - 1) {
                pdpNavigation.showNext = false;
              }
            } else {
              const offset = parseInt(this.getQSParam("offset", url) || 0, 10);
              if (currentIndex === 0 && offset === 0) {
                pdpNavigation.showPrevious = false;
              }
            }
          }
        } else {
          pdpNavigation.showPrevious = false;
          pdpNavigation.showNext = false;
        }
      }
    }

    this.state = {
      clientLoaded: false,
      favorites: props.favorites || [],
      openDropdowns: {},
      leadCampaignTracking: {},
      pdpNavigation,
    };
  }

  componentDidMount() {
    // Needed for SSR hydration diff warning: https://reactjs.org/docs/react-dom.html#hydrate
    this.setState({
      clientLoaded: true,
    });
  }

  assertCaptcha = (action: string): string => {
    const { RECAPTCHA_CLIENT } = this.props;
    return new Promise((resolve) => {
      const { grecaptcha } = window;
      grecaptcha.ready(() => {
        grecaptcha.execute(RECAPTCHA_CLIENT, { action }).then((token) => {
          resolve(token);
        });
      });
    });
  };

  assertCaptchaV2 = () => {
    if (typeof window === "undefined") {
      return false;
    }
    const { RECAPTCHA_CLIENT_V2 } = this.props;
    const { grecaptcha } = window;
    // $FlowIgnore
    document.addEventListener("DOMContentLoaded", () => {
      grecaptcha.render("recaptcha-v2-dom", {
        sitekey: RECAPTCHA_CLIENT_V2,
      });
    });
  };

  isPageForceV2Captcha = () => {
    if (typeof window === "undefined") {
      return false;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const myParam = urlParams.get("force-v2-captcha");
    return myParam === "true";
  };

  onToggleDropdown = (e, name: string) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const openDropdowns = { ...this.state.openDropdowns };

    openDropdowns &&
      !openDropdowns.navbar &&
      Object.keys(openDropdowns).forEach((key) => {
        if (key === name) {
          return;
        } else {
          openDropdowns[key] = false;
        }
      });

    openDropdowns[name] = !openDropdowns[name];
    this.setState({ openDropdowns });
  };

  onCloseDropdown = () => {
    const openDropdowns = { ...this.state.openDropdowns };

    Object.keys(openDropdowns).forEach((key) => {
      if (openDropdowns[key]) {
        openDropdowns[key] = false;
      }
    });

    this.setState({ openDropdowns });
  };

  navigateBack = () => {
    let pdpStack;
    try {
      pdpStack = localStorage.getItem("pdpStack");
    } catch (e) {}
    if (pdpStack) {
      pdpStack = JSON.parse(pdpStack);
      if (pdpStack.length > 0) {
        let pdpNavigation = pdpStack.pop();
        localStorage.setItem("pdpStack", JSON.stringify(pdpStack));

        window.location.href = pdpNavigation.url;
      }
    }
  };

  navigateNext = () => {
    let pdpStack;
    try {
      pdpStack = localStorage.getItem("pdpStack");
    } catch (e) {}
    if (pdpStack) {
      pdpStack = JSON.parse(pdpStack);
      if (pdpStack.length > 0) {
        const pdpList = pdpStack[pdpStack.length - 1].pdpList;
        const action = pdpStack[pdpStack.length - 1].action;
        const url = pdpStack[pdpStack.length - 1].url;
        const currentIndex = pdpList.indexOf(window.location.pathname);

        if (currentIndex + 1 < pdpList.length) {
          window.location.href = pdpList[currentIndex + 1];
        } else if (action === "search") {
          const offset = parseInt(this.getQSParam("offset", url) || 0, 10);
          const searchUrl = this.updateQSParam(url, "offset", offset + 12);
          axios({
            url:
              "/api/searchListings" +
              searchUrl.substring(searchUrl.indexOf("?")),
            method: "GET",
          }).then((response) => {
            if (response && response.status === 200) {
              if (
                response.data &&
                response.data.newPagePdpList &&
                response.data.newPagePdpList.length
              ) {
                pdpStack[pdpStack.length - 1].pdpList = [];
                response.data.newPagePdpList.forEach((element) => {
                  pdpStack[pdpStack.length - 1].pdpList.push(
                    "/listings/" + element
                  );
                });

                pdpStack[pdpStack.length - 1].url = searchUrl;
              } else {
                // $(".jq__pdp-next").hide();
              }

              localStorage.setItem("pdpStack", JSON.stringify(pdpStack));

              window.location.href = pdpStack[pdpStack.length - 1].pdpList[0];
            }
          });
        }
      }
    }
  };

  navigatePrevious = () => {
    let pdpStack;
    try {
      pdpStack = localStorage.getItem("pdpStack");
    } catch (e) {}
    if (pdpStack) {
      pdpStack = JSON.parse(pdpStack);
      if (pdpStack.length > 0) {
        const pdpList = pdpStack[pdpStack.length - 1].pdpList;
        const action = pdpStack[pdpStack.length - 1].action;
        const url = pdpStack[pdpStack.length - 1].url;
        const currentIndex = pdpList.indexOf(window.location.pathname);

        if (currentIndex > 0) {
          window.location.href = pdpList[currentIndex - 1];
        } else if (action === "search") {
          const offset = parseInt(this.getQSParam("offset", url) || 0, 10);
          if (offset === 0) {
            this.setState((state) => ({
              pdpNavigation: {
                ...state.pdpNavigation,
                showPrevious: false,
              },
            }));
            return;
          }
          const searchUrl = this.updateQSParam(url, "offset", offset - 12);
          axios({
            url:
              "/api/searchListings" +
              searchUrl.substring(searchUrl.indexOf("?")),
            method: "GET",
          }).then((response) => {
            if (response && response.status === 200) {
              if (
                response.data &&
                response.data.newPagePdpList &&
                response.data.newPagePdpList.length
              ) {
                pdpStack[pdpStack.length - 1].pdpList = [];
                response.data.newPagePdpList.forEach((element) => {
                  pdpStack[pdpStack.length - 1].pdpList.push(
                    "/listings/" + element
                  );
                });

                pdpStack[pdpStack.length - 1].url = searchUrl;
              } else {
                this.setState((state) => ({
                  pdpNavigation: {
                    ...state.pdpNavigation,
                    showPrevious: false,
                  },
                }));
              }

              localStorage.setItem("pdpStack", JSON.stringify(pdpStack));

              window.location.href =
                pdpStack[pdpStack.length - 1].pdpList[pdpList.length - 1];
            }
          });
        }
      }
    }
  };

  onPdpNavigation = ({ currentUrl, url, action, pdpList }) => {
    let pdpStack;
    try {
      pdpStack = localStorage.getItem("pdpStack");
    } catch (e) {
      return (window.location.href = url);
    }
    if (!pdpStack) {
      pdpStack = [];
    } else {
      pdpStack = JSON.parse(pdpStack);
    }
    pdpStack.push({ url: currentUrl, action, pdpList });
    localStorage.setItem("pdpStack", JSON.stringify(pdpStack));

    window.location.href = url;
  };

  getQSParam = (name, url) => {
    name = name.replace(/[[\]]/g, "\\$&");
    const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  };

  updateQSParam = (url, key, value) => {
    const regex = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
    const separator = url.indexOf("?") !== -1 ? "&" : "?";
    if (url.match(regex)) {
      return url.replace(regex, "$1" + key + "=" + value + "$2");
    } else {
      return url + separator + key + "=" + value;
    }
  };

  isCanadian = () => {
    const site = this.props.site;
    const searchArr = site ? site.datastoreProviderIds : [];
    const candianProviderIds = searchArr.find((n) => n === 156 || n === 233);
    return Boolean(candianProviderIds);
  };

  // function for List Trac when needed for Compliance

  toggleFavoriteListing = (listingId, mlsListingId, zipCode, hasListTrac) => {
    if (!listingId || !mlsListingId) {
      return null;
    }

    const { siteUser } = this.props;
    if (!(siteUser && siteUser.id)) {
      // Redirect to login if user isn't logged in already
      return (window.location = `/signup?action=favorite&listingId=${listingId}&redirect=${encodeURIComponent(
        window.location.href
      )}`);
    } else {
      // if they have ListTrac enabled, we want to log the favorite event here
      // only loaded on PDP and if compliance warrants it
      if (hasListTrac) {
        // only log this event if this listing has not been favorited
        const isFavorited =
          this.state.favorites && this.state.favorites.indexOf(listingId) > -1;
        if (!isFavorited) {
          listTracLogEventFavorite({ mlsListingId, zipCode });
        }
      }
      // Make request to update favorites if the user is already logged in
      return axios({
        data: {
          mlsListingId,
        },
        method: "POST",
        url: `/api/listings/${listingId}/favorite`,
      }).then((response) => {
        if (response.status === 204) {
          // Update local state so we don't need to refresh the page
          this.setState((state) => {
            let favorites = [...state.favorites];
            const favoriteIndex = favorites.indexOf(listingId);
            if (favoriteIndex > -1) {
              return {
                favorites: [
                  ...favorites.slice(0, favoriteIndex),
                  ...favorites.slice(favoriteIndex + 1, favorites.length),
                ],
              };
            } else {
              favorites.push(listingId);
              return {
                favorites,
              };
            }
          });
        }
        return;
      });
    }
  };

  trackLeadAction = (trackingObject) => {
    if (
      !(trackingObject && (trackingObject.listingId || trackingObject.action))
    ) {
      return null;
    }

    const { siteUser } = this.props;
    if (!(siteUser && siteUser.id)) {
      return null;
    } else {
      // Make request to track actions if the user is already logged in
      return axios({
        data: trackingObject,
        method: "POST",
        url: "/api/leads/listing",
      });
    }
  };

  onTrackCampaign = (campaignTracking) => {
    if (Object.keys(campaignTracking).length !== 0) {
      let leadCampaignTracking = {
        utm_campaign: campaignTracking.utm_campaign || "",
        utm_medium: campaignTracking.utm_medium || "",
        utm_source: campaignTracking.utm_source || "",
        utm_term: campaignTracking.utm_term || "",
        utm_content: campaignTracking.utm_content || "",
      };

      localStorage.setItem(
        "leadCampaignTracking",
        JSON.stringify(leadCampaignTracking)
      );
    }
  };

  onReadCampaign = () => {
    try {
      const trackingObj = JSON.parse(
        localStorage.getItem("leadCampaignTracking")
      );
      return trackingObj;
    } catch (e) {
      return {};
    }
  };

  render() {
    const {
      BATHS_OPTIONS,
      BEDS_OPTIONS,
      CDN_IMAGE_SERVER_URL,
      PRICE_OPTIONS,
      RECAPTCHA_CLIENT,
      RECAPTCHA_CLIENT_V2,
      aboutLink,
      aboutPosts,
      agent,
      compliance,
      footerPod,
      headerNav,
      isEqualHousingOpportunity,
      mapProvider,
      office,
      openHouses,
      pathName,
      previousFormFieldValues,
      privacyPolicy,
      accessibility,
      site,
      siteUser,
      team,
      tenantConfig,
      theme,
      withStyleReset = true,
      brokers,
      mlsApiVersion,
    } = this.props;
    const {
      clientLoaded,
      favorites,
      openDropdowns,
      pdpNavigation,
      leadCampaignTracking,
    } = this.state;

    return (
      <ThemeProvider
        theme={{
          colors: {
            primary:
              (theme &&
                theme.primaryColor &&
                generatePalette(theme.primaryColor)) ||
              generatePalette("#232c35"),
            primaryFontColor: (theme && theme.primaryFontColor) || "#fff", // I don't think this key does anything in elevate-ui
            secondary:
              (theme &&
                theme.secondaryColor &&
                generatePalette(theme.secondaryColor)) ||
              generatePalette("#4b565f"),
            secondaryFontColor: (theme && theme.secondaryFontColor) || "#fff", // I don't think this key does anything in elevate-ui
          },
          zIndex,
          overrides: {
            EuiButtonGroup: {
              button: {
                border: "1px solid #dde2e7",
                padding: "16px",

                "&:first-of-type": {
                  borderLeft: "1px solid #dde2e7",
                  borderTopLeftRadius: "6px",
                  borderBottomLeftRadius: "6px",
                },
                "&:last-of-type": {
                  borderRight: "1px solid #dde2e7",
                  borderTopRightRadius: "6px",
                  borderBottomRightRadius: "6px",
                },
                "& span": {
                  pointerEvents: "none",
                  color: "#4b565f",
                },
              },

              buttonActive: {
                border: "1px solid #dde2e7",
                padding: "16px",

                "&:first-of-type": {
                  borderLeft: "1px solid #dde2e7",
                  borderTopLeftRadius: "6px",
                  borderBottomLeftRadius: "6px",
                },
                "&:last-of-type": {
                  borderRight: "1px solid #dde2e7",
                  borderTopRightRadius: "6px",
                  borderBottomRightRadius: "6px",
                },
              },
            },
            EuiInput: {
              root: {
                height: "48px",
                border: "1px solid #dde2e7",
                borderRadius: "6px",
              },
            },
            EuiCurrencyInput: {
              root: {
                height: "48px",
                border: "1px solid #dde2e7",
                borderRadius: "6px",
              },
            },
            EuiMultiSelect: {
              root: {
                minHeight: "48px", // MultiSelect uses a min-height rather than height because of the way tags overflow-scroll left/right
                border: "1px solid #dde2e7",
                borderRadius: "6px",
              },
            },
            EuiNumberIncrement: {
              input: {
                border: "1px solid #dde2e7",
              },
            },
            EuiSelect: {
              root: {
                height: "48px",
                border: "1px solid #dde2e7",
                borderRadius: "6px",
              },
            },
            EuiTextarea: {
              root: {
                border: "1px solid #dde2e7",
                height: "48px",
                borderRadius: "6px",
              },
            },
          },
        }}
        withReset={withStyleReset}
      >
        <GlobalContext.Provider
          value={{
            BATHS_OPTIONS,
            BEDS_OPTIONS,
            CDN_IMAGE_SERVER_URL,
            PRICE_OPTIONS,
            RECAPTCHA_CLIENT,
            RECAPTCHA_CLIENT_V2,
            aboutLink,
            aboutPosts,
            agent,
            clientLoaded,
            compliance,
            favorites,
            footerPod,
            headerNav,
            isEqualHousingOpportunity,
            mapProvider,
            office,
            onCloseDropdown: this.onCloseDropdown,
            onPdpNavigation: this.onPdpNavigation,
            onToggleDropdown: this.onToggleDropdown,
            openDropdowns,
            openHouses,
            pathName,
            pdpNavigation,
            previousFormFieldValues,
            privacyPolicy,
            accessibility,
            site,
            siteUser,
            team,
            tenantConfig,
            brokers,
            toggleFavoriteListing: this.toggleFavoriteListing,
            isCanadian: this.isCanadian,
            trackLeadAction: this.trackLeadAction,
            assertCaptcha: this.assertCaptcha,
            assertCaptchaV2: this.assertCaptchaV2,
            isPageForceV2Captcha: this.isPageForceV2Captcha,
            onTrackCampaign: this.onTrackCampaign,
            onReadCampaign: this.onReadCampaign,
            leadCampaignTracking,
            mlsApiVersion,
          }}
        >
          {this.props.children}
        </GlobalContext.Provider>
      </ThemeProvider>
    );
  }
}

export const withGlobalContext = (Component: React$Element<*>) => (
  props: *
) => (
  <GlobalContext.Consumer>
    {(globalContext) => <Component {...globalContext} {...props} />}
  </GlobalContext.Consumer>
);

// For ListTrac Compliance
const listTracLogEventFavorite = ({ mlsListingId, zipCode }) => {
  if (window._LT) {
    window._LT._trackEvent(window._eventType.favorite, mlsListingId, zipCode);
  }
};
